import React, { useState, useEffect } from 'react';
import { CardMedia, Box } from '@mui/material';
import { DEFAULT_CARD_IMAGE, CARD_EFFECTS, getCardImageUrl, isArcana, isGoldArcana, isBlackArcana, needsGlimmerEffect } from './cardImageUtils';


/**
 * Optimierte CardImage Komponente mit nativer Lazy-Loading-Unterstützung
 */
const LazyCardImage = ({
  card,
  details,
  showBaseImages,
  alt,
  showArcanaEffect = true,
  customLevel = null, // Optionales spezifisches Level
  sx = {}
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const primaryUrl = getCardImageUrl(card, details, showBaseImages, customLevel);
  const imageUrl = hasError ? DEFAULT_CARD_IMAGE : primaryUrl;

  // Nur zurücksetzen, wenn sich die tatsächliche Bild-URL ändert - sonst feuert
  // der Browser kein neues "load"-Event (gleicher src) und isLoaded bleibt für
  // immer false, obwohl das Bild bereits angezeigt wird (Spinner haengt fest).
  useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
  }, [primaryUrl]);

  // Prüfe, ob es eine Arcana-Karte ist
  const isCardArcana = isArcana(card, details);
  
  // Prüfe, ob es einen Arcana-Overlay braucht
  // Verwenden der needsGlimmerEffect-Funktion aus den cardImageUtils
  const needsArcanaOverlay = needsGlimmerEffect(card, details);

  // Placeholder während des Ladens
  const placeholderStyles = {
    backgroundColor: '#2a2a2a',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  };

  // Behandlung von Bild-Ladefehlern
  const handleImageError = () => {
    setHasError(true);
  };

  // Erfolgreiches Laden des Bildes
  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  return (
    <Box position="relative" width="100%" height="100%" className="card-image-container">
      {/* Bild-Placeholder, wenn noch nicht geladen */}
      {!isLoaded && (
        <Box style={placeholderStyles} className="card-placeholder">
          <Box sx={{ 
            width: '50px',
            height: '50px',
            borderRadius: '50%', 
            border: '3px solid #555',
            borderTopColor: '#888',
            animation: 'spin 1s linear infinite'
          }} className="loading-spinner" />
        </Box>
      )}
      {/* Das Kartenbild */}
      <CardMedia
        key={imageUrl}
        component="img"
        sx={{
          height: '100%',
          width: '100%',
          objectFit: 'contain',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
          ...sx
        }}
        image={imageUrl}
        alt={alt || 'Splinterlands Card'}
        onError={handleImageError}
        onLoad={handleImageLoad}
        className="card-image"
      />
      {/* Arcana-Effekt als Überlagerung für Arcana-Karten, Gold Arcana (foil=2) und Black Arcana (foil=4) */}
      {showArcanaEffect && isLoaded && (isCardArcana || needsArcanaOverlay) && (
        <Box 
          component="img"
          src={CARD_EFFECTS.ARCANA_GLIMMER}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0.85,
            animation: 'arcana-glimmer 8s infinite ease-in-out, arcana-pulse 15s infinite ease-in-out',
            '@keyframes arcana-glimmer': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 0.95 },
              '100%': { opacity: 0.6 }
            },
            '@keyframes arcana-pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.02)' },
              '100%': { transform: 'scale(1)' }
            }
          }}
          className="arcana-effect"
          alt={`${alt || 'Splinterlands Card'} - effects`}
        />
      )}
    </Box>
  );
};

export default LazyCardImage;
